import type { GameConnectionInfo, TabId, TrainingInfo } from '../types/app';
import type { TableCategory } from '../contexts/AppContext';

export type BetaFeedbackOccurrence = 'first_time' | 'recurring' | 'unknown';

const TAB_LABELS: Record<TabId, string> = {
  home: 'Accueil',
  chat: 'Discussion',
  tables: 'Tables',
  game: 'Partie',
  rankings: 'Classements',
  competitions: 'Compétitions',
  settings: 'Paramètres',
  training: 'Entraînement',
  library: 'Bibliothèque',
  forums: 'Forums',
  admin: 'Administration',
  subscription: 'Abonnement',
  mySubscription: 'Mon abonnement',
};

const GAME_PHASE_LABELS: Record<number, string> = {
  1: 'Démarrage',
  2: 'Enchères',
  3: 'Appel du Roi',
  4: 'Le Chien',
  5: 'Écart',
  6: 'Jeu',
  99999: 'Fin de donne',
};

let pageDetail: Record<string, unknown> | null = null;

/** Enregistre le contexte détaillé de la page active (partie, entraînement…). */
export function setBetaPageDetail(detail: Record<string, unknown> | null): void {
  pageDetail = detail;
}

export function getBetaPageDetail(): Record<string, unknown> | null {
  return pageDetail;
}

function summarizeGameConnection(gameInfo: GameConnectionInfo): Record<string, unknown> {
  const table = gameInfo.table;
  return {
    idserv: gameInfo.idserv ?? null,
    neo: gameInfo.neo ?? false,
    neoTableId: gameInfo.neoTableId ?? null,
    spectatorMode: gameInfo.spectatorMode ?? false,
    hasOtherHumans: gameInfo.hasOtherHumans ?? null,
    retroDiffere: gameInfo.retroDiffere
      ? {
          sessionId: gameInfo.retroDiffere.sessionId,
          dealIndex: gameInfo.retroDiffere.dealIndex,
          seasonId: gameInfo.retroDiffere.seasonId,
          rejoin: gameInfo.retroDiffere.rejoin,
          totalDeals: gameInfo.retroDiffere.totalDeals ?? null,
        }
      : null,
    table: table
      ? {
          type: table.type,
          idserv: table.idserv,
          nbj: table.nbj,
          nomtable: table.nomtable ?? null,
          comm: table.comm ?? null,
          playerCount: table.joueurs?.length ?? 0,
        }
      : null,
  };
}

function summarizeTraining(trainingInfo: TrainingInfo): Record<string, unknown> {
  return {
    sessionId: trainingInfo.sessionId,
    displayMode: trainingInfo.displayMode,
    showTimeline: trainingInfo.showTimeline,
    disabledBots: [...trainingInfo.botConfig.disabledBots],
    constraints: trainingInfo.constraints
      ? {
          handQualities: trainingInfo.constraints.handQualities ?? null,
          forcedBids: trainingInfo.constraints.forcedBids ?? null,
        }
      : null,
  };
}

function summarizeTables(categories: TableCategory[]): Record<string, unknown> {
  return {
    categoryCount: categories.length,
    tableCount: categories.reduce((sum, cat) => sum + cat.tables.length, 0),
    playerCount: categories.reduce((sum, cat) => sum + cat.playerCount, 0),
    categories: categories.map(cat => ({
      name: cat.name,
      tableCount: cat.tables.length,
      playerCount: cat.playerCount,
    })),
  };
}

export function getCurrentPageLabel(activeTab: TabId): string {
  return TAB_LABELS[activeTab] ?? activeTab;
}

export interface BuildBetaFeedbackContextInput {
  activeTab: TabId;
  gameInfo: GameConnectionInfo | null;
  trainingInfo: TrainingInfo | null;
  tablesCategories?: TableCategory[];
  chatUnreadCount?: number;
  chatConnected?: boolean;
  concernsCurrentPage: boolean;
  occurrence?: BetaFeedbackOccurrence;
  rating?: number | null;
}

export function buildBetaFeedbackContext(input: BuildBetaFeedbackContextInput): Record<string, unknown> {
  const {
    activeTab,
    gameInfo,
    trainingInfo,
    tablesCategories,
    chatUnreadCount,
    chatConnected,
    concernsCurrentPage,
    occurrence,
    rating,
  } = input;

  const base: Record<string, unknown> = {
    url: window.location.pathname + window.location.search + window.location.hash,
    userAgent: navigator.userAgent,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
    },
    concernsCurrentPage,
  };

  if (occurrence) {
    base.occurrence = occurrence;
  }
  if (typeof rating === 'number' && rating >= 0 && rating <= 5) {
    base.rating = rating;
  }

  if (!concernsCurrentPage) {
    return base;
  }

  const page: Record<string, unknown> = {
    activeTab,
    tabLabel: TAB_LABELS[activeTab] ?? activeTab,
    inGame: !!gameInfo,
    inTraining: !!trainingInfo,
  };

  if (gameInfo) {
    page.game = summarizeGameConnection(gameInfo);
  }
  if (trainingInfo) {
    page.training = summarizeTraining(trainingInfo);
  }
  if (activeTab === 'tables' && tablesCategories) {
    page.tables = summarizeTables(tablesCategories);
  }
  if (activeTab === 'chat') {
    page.chat = {
      unreadCount: chatUnreadCount ?? 0,
      connected: chatConnected ?? false,
    };
  }

  const detail = getBetaPageDetail();
  if (detail) {
    page.detail = detail;
    if (typeof detail.phase === 'number') {
      page.phaseLabel = GAME_PHASE_LABELS[detail.phase] ?? `Phase ${detail.phase}`;
    } else if (typeof detail.phase === 'string') {
      page.phaseLabel = detail.phase;
    }
  }

  return {
    ...base,
    page,
  };
}

export function getPageContextExtraDetails(input: Omit<BuildBetaFeedbackContextInput, 'concernsCurrentPage'>): string | null {
  const context = buildBetaFeedbackContext({ ...input, concernsCurrentPage: true });
  const page = context.page;
  if (!page || typeof page !== 'object') return null;
  const p = page as Record<string, unknown>;
  const parts: string[] = [];
  if (p.phaseLabel) parts.push(String(p.phaseLabel));
  const detail = p.detail;
  if (detail && typeof detail === 'object') {
    const d = detail as Record<string, unknown>;
    if (d.numPli != null) parts.push(`pli ${Number(d.numPli) + 1}`);
    if (d.dealNumber != null) parts.push(`donne ${d.dealNumber}`);
    if (d.currentContract != null) parts.push(`contrat ${d.currentContract}`);
  }
  if (p.inGame) parts.push('partie en cours');
  if (p.inTraining) parts.push('entraînement actif');
  return parts.length > 0 ? parts.join(' · ') : null;
}

export function getFeedbackPageLabel(context: Record<string, unknown>): string {
  if (context.concernsCurrentPage !== true) return 'Neo (global)';
  const page = context.page;
  if (page && typeof page === 'object') {
    const tabLabel = (page as Record<string, unknown>).tabLabel;
    if (typeof tabLabel === 'string' && tabLabel.length > 0) return tabLabel;
  }
  return 'Page inconnue';
}

export function getFeedbackPageKey(context: Record<string, unknown>): string {
  if (context.concernsCurrentPage !== true) return '__global__';
  const page = context.page;
  if (page && typeof page === 'object') {
    const tabLabel = (page as Record<string, unknown>).tabLabel;
    if (typeof tabLabel === 'string' && tabLabel.length > 0) return tabLabel;
    const activeTab = (page as Record<string, unknown>).activeTab;
    if (typeof activeTab === 'string') return activeTab;
  }
  return '__unknown__';
}

export function formatBetaPageContextSummary(context: Record<string, unknown>): string | null {
  const page = context.page;
  if (!page || typeof page !== 'object') return null;
  const p = page as Record<string, unknown>;
  const parts: string[] = [];
  if (p.tabLabel) parts.push(String(p.tabLabel));
  if (p.phaseLabel) parts.push(String(p.phaseLabel));
  const detail = p.detail;
  if (detail && typeof detail === 'object') {
    const d = detail as Record<string, unknown>;
    if (d.numPli != null) parts.push(`pli ${Number(d.numPli) + 1}`);
    if (d.dealNumber != null) parts.push(`donne ${d.dealNumber}`);
    if (d.currentContract != null) parts.push(`contrat ${d.currentContract}`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}
